import React, { FC } from 'react';
import { Trans } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import Yup from 'utils/yup';
import { t } from 'i18next';
import { DateTime } from 'luxon';

import ReplayIcon from '@mui/icons-material/Replay';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Card, Grid, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import {
  Button,
  ColumnProps,
  FileUpload,
  LeoSelectField,
  Page,
  Table,
  useFilterConfig,
  usePanelState,
  useTableQueryParams,
} from 'components';
import { FilterOptionModel, JobModel } from 'models';
import { useValueSetList } from 'features/valueSets';
import { useFieldDataAndAccessor } from 'utils/useFieldDataAndAccessor';
import { getValueSetValues } from 'utils/valueSetFunctions';
import {
  useActiveTask,
  useDownloadResult,
  useJobStatus,
  useLoadTask,
  useLoadTaskList,
  useRetryLoadTask,
} from 'features/loadTasks';
import { getPageName } from 'utils/format';
import { getListParams } from 'utils/queryHelpers';
import { RouteNames } from 'config/routeNames';
import { DATE_TIME_FORMAT, formatDate } from 'utils/dates';

const styles = {
  uploadCard: {
    p: 2,
    borderRadius: '8px',
    '@media (min-width: 1600px)': {
      width: '35%',
    },
    '@media (max-width: 1599px)': {
      width: '45%',
    },
  },
  table: {
    '.MuiPaper-root': {
      '@media (min-height: 900px)': {
        maxHeight: '400px',
      },
      '@media (max-height: 899px)': {
        maxHeight: '265px',
      },
    },
  },
};

const LoadTasksFromExcelPage: FC = () => {
  const {
    onPageChange,
    onRowsPerPageChange,
    onFilter,
    onLoadQuery,
    onSort,
    params,
    onTableColumnVisibilityChange,
    apiParams,
  } = useTableQueryParams();

  const { data: valueSet, isLoading: isValueSetLoading } = useValueSetList({
    code: ['BACKGROUND_JOB_STATUS'],
  });

  const { data: backgroundJobStatus, accessor: backgroundJobStatusAccessor } =
    useFieldDataAndAccessor(getValueSetValues(valueSet, 'BACKGROUND_JOB_STATUS'));

  const {
    data: loadTaskListData,
    isLoading: isTableDataLoading,
    isFetching,
    dataUpdatedAt,
  } = useLoadTaskList(getListParams(apiParams, ['status']));
  const { mutateAsync: upLoadTask, isPending: isUploading, data: uploadData } = useLoadTask();
  const { mutateAsync: retryLoadTask, isPending: isRetryLoading } = useRetryLoadTask();
  const { mutateAsync: downloadResult, isPending: isDownloading } = useDownloadResult();
  const { data: activeTask } = useActiveTask();
  const { data: jobStatus } = useJobStatus(activeTask?.job?.id || uploadData?.jobId || '');
  const hasActiveJob = jobStatus?.status && jobStatus?.status !== 'completed';

  const filters: FilterOptionModel[] = [
    {
      headerTitle: 'KOZOS_FELADAT_BETOLTES_EXCELBOL.STATUS',
      component: (
        <LeoSelectField
          name="status"
          data={backgroundJobStatus}
          accessor={backgroundJobStatusAccessor}
          label="KOZOS_FELADAT_BETOLTES_EXCELBOL.STATUS"
        />
      ),
      name: 'status',
      panelFieldWidth: 12,
      abbrName: t('FILTERABBR.STATUS'),
      filterIsRequired: true,
    },
  ];

  const validationSchema = Yup.object().shape({
    status: Yup.mixed().required(),
  });

  const filterConfig = useFilterConfig({
    params,
    onFilter,
    filters,
    onLoad: onLoadQuery,
    isButton: true,
    pageName: getPageName(RouteNames.KOZOS_FELADAT_BETOLTES_EXCELBOL),
    validationSchema,
    isFetching,
    defaultFilter: { status: 'active' },
  });

  const columns: ColumnProps<JobModel>[] = [
    {
      key: 'id',
      header: <Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.ID" />,
      accessor: 'id',
      sortable: false,
    },
    {
      key: 'data.originalFileName',
      header: <Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.FILE_NAME" />,
      accessor: 'data.originalFileName',
      sortable: false,
    },
    {
      key: 'processedOn',
      header: <Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.PROCESS_TIME" />,
      accessor: (item: JobModel) =>
        item.processedOn && formatDate(DateTime.fromMillis(item.processedOn), DATE_TIME_FORMAT),
      field: 'processedOn',
      sortable: false,
    },
    {
      key: 'finishedOn',
      header: <Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.FINISH_TIME" />,
      accessor: (item: JobModel) =>
        item.finishedOn && formatDate(DateTime.fromMillis(item.finishedOn), DATE_TIME_FORMAT),
      field: 'finishedOn',
      sortable: false,
    },
    {
      key: 'status',
      header: <Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.STATUS" />,
      accessor: (item: JobModel) =>
        item.status && getValueSetValues(valueSet, 'BACKGROUND_JOB_STATUS', item.status),
      field: 'status',
      sortable: false,
    },
  ];

  const panels = [filterConfig];
  const { activePanel, closePanel, setPanel } = usePanelState();

  const actions = [
    {
      name: 'retry',
      label: 'KOZOS_FELADAT_BETOLTES_EXCELBOL.RETRY',
      icon: <ReplayIcon fontSize="small" />,
      disabledIf: isRetryLoading,
      onClick: (job: JobModel) => retryLoadTask(job.id),
    },
    {
      name: 'download',
      label: 'KOZOS_FELADAT_BETOLTES_EXCELBOL.DOWNLOAD',
      icon: <DownloadIcon fontSize="small" />,
      disabledIf: (job: JobModel) => {
        return isDownloading || job.status === 'active' || job.status === 'failed';
      },
      onClick: (job: JobModel) => downloadResult(job.id),
    },
  ];

  return (
    <Page
      title={<Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.TITLE" />}
      inScroll
      panels={panels}
      activePanel={activePanel}
      closePanel={closePanel}
      setPanel={setPanel}
    >
      <Card elevation={1} sx={styles.uploadCard}>
        {!hasActiveJob && (
          <Formik
            initialValues={{ file: null }}
            onSubmit={async (values, { resetForm }) => {
              await upLoadTask(values?.file?.[0]);
              resetForm();
            }}
          >
            {({ values, resetForm }) => (
              <Form>
                <Field
                  label={<Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.FILE_LABEL" />}
                  component={FileUpload}
                  multiple={false}
                  onDropRejected={resetForm}
                  name="file"
                  accept={{
                    'application/json': ['.xls', '.xlsx'],
                  }}
                />
                <Box mt={2}>
                  <Button type="submit" disabled={!values.file} loading={isUploading}>
                    <Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.IMPORT_BUTTON" />
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        )}
        {hasActiveJob && jobStatus && (
          <>
            <Grid container spacing={1}>
              <Grid item xs={10}>
                <Typography variant="body1">
                  <Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.ACTIVE_JOB" />
                </Typography>
              </Grid>
              <Grid item xs={2} textAlign={'right'}>
                <CircularProgress size={24} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.ID" />
                </Typography>
                <Typography variant="body2">{jobStatus.id}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.FILE_NAME" />
                </Typography>
                <Typography variant="body2">{jobStatus.data.originalFileName}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.PROCESSED_ROWS" />
                </Typography>
                <Typography variant="body2">{jobStatus.data.completedRows}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Trans i18nKey="KOZOS_FELADAT_BETOLTES_EXCELBOL.FAILED_ROWS" />
                </Typography>
                <Typography variant="body2">{jobStatus.data.failedRows}</Typography>
              </Grid>
            </Grid>
          </>
        )}
      </Card>
      <Box sx={styles.table}>
        {!isValueSetLoading && (
          <Table
            filterDetailsData={{ backgroundJobStatusAccessor }}
            setPanel={setPanel}
            activePanel={activePanel}
            list={loadTaskListData?.data || []}
            total={loadTaskListData?.meta.total}
            timestamp={dataUpdatedAt}
            loading={isTableDataLoading}
            params={params}
            onSort={onSort}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            enableCheckbox={false}
            columns={columns}
            onTableColumnVisibilityChange={onTableColumnVisibilityChange}
            disableDivider={true}
            onLoadQuery={onLoadQuery}
            actions={actions}
            panels={panels}
          />
        )}
      </Box>
    </Page>
  );
};

export default LoadTasksFromExcelPage;
